// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ResponsiveScatterPlot, type ScatterPlotNodeData } from '@nivo/scatterplot';
import { type RiskReturns } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { ChartContainer } from '@shared/ui-kit/components';
import { Legend } from '../legend';
import { useCallback, useMemo } from 'react';
import { RiskVsReturnsGraphTooltip } from './risk-vs-returns-graph-tooltip';
import { SERIES_COLORS } from '@shared/ui-kit/utils';
import { useTranslation } from 'react-i18next';

export interface RiskVsReturnsGraphProps {
  risks: Array<RiskReturns & { name?: string }>;
  renderTitle?: boolean;
  benchmark?: string;
}

export const RiskVsReturnsGraph = ({
  risks = [],
  benchmark,
}: RiskVsReturnsGraphProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      risks
        .filter(risk => risk.volatility >= 0)
        .map(risk => ({
          id: risk.ticker === benchmark ? 'BENCHMARK' : risk.ticker,
          name: risk.name || risk.ticker,
          data: [{ x: risk.volatility * 100, y: risk.returns * 100 }],
        })),
    [benchmark, risks],
  );

  const maxRisk = useMemo(
    () => Math.max(...risks.map(risk => risk.volatility * 100)) * 1.3,
    [risks],
  );
  const maxReturn = useMemo(
    () => Math.max(...risks.map(risk => risk.returns * 100)) * 1.3,
    [risks],
  );

  const getNodeSize = useCallback(
    (
      node: Omit<
        ScatterPlotNodeData<{
          x: number;
          y: number;
        }>,
        'color' | 'size'
      >,
    ): number => {
      switch (node.serieId) {
        case 'Portfolio':
        case 'Benchmark':
          return 30;
        default:
          return 10;
      }
    },
    [],
  );

  return (
    <ChartContainer
      header={t('research.risk-vs-return', { ns: 'research' })}
      popupName="charts.riskVsReturn"
      tooltip
      data-testid="risk-vs-returns-graph"
    >
      <div className="mt-0 h-[420px]">
        <div className="flex flex-wrap justify-end gap-x-7 gap-y-2">
          {data.map((position, index) => (
            <Legend
              key={position.id}
              label={position.id}
              color={SERIES_COLORS[index]}
              tooltip={position.name}
            />
          ))}
        </div>
        <ResponsiveScatterPlot
          data={data}
          margin={{ top: 20, right: 20, bottom: 70, left: 50 }}
          xScale={{ type: 'linear', min: 0, max: Math.max(maxRisk, 30) }}
          yScale={{ type: 'linear', min: -30, max: Math.max(maxReturn, 30) }}
          xFormat=">-.2f"
          colors={SERIES_COLORS}
          key={data.length}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickValues: 5,
            legend: t('common.risk%'),
            legendPosition: 'middle',
            legendOffset: 44,
          }}
          yFormat=">-.2f"
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            legend: t('common.return%'),
            legendPosition: 'middle',
            legendOffset: -44,
            tickValues: 5,
          }}
          blendMode="multiply"
          axisTop={null}
          axisRight={null}
          theme={{
            axis: {
              legend: {
                text: {
                  fontSize: '1em',
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
              ticks: {
                text: {
                  fontSize: '1em',
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
            },
          }}
          useMesh={false}
          nodeSize={getNodeSize}
          tooltip={RiskVsReturnsGraphTooltip}
          animate={false}
        />
      </div>
    </ChartContainer>
  );
};
